// markup/useMarkup.js — DropPin plan-markup React state controller.
// ---------------------------------------------------------------------------
// The single stateful brain wiring the pure modules (model / clipboard /
// history / measure / edit) to React. Owns the objects (through an undo/redo
// history), selection, active tool, draw style, unit + calibration, the in-app
// clipboard, snapping prefs, the take-off panel toggle, keyboard shortcuts, and
// per-drawing persistence (localStorage always; an optional server `remote`
// layers on top — see below). Rendering + pointer input live in MarkupLayer;
// the palette lives in MarkupToolbar. No geometry math here — it is all in the
// pure modules.
// ---------------------------------------------------------------------------

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  translateObject, serialize, deserialize, hitTestTopmost as modelHitTopmost, mergePatches,
} from './model.mjs';
import { copyObjects, cutObjects, pasteObjects } from './clipboard.mjs';
import {
  createHistory, pushHistory, replacePresent, commitInteraction,
  undo as hUndo, redo as hRedo, canUndo as hCanUndo, canRedo as hCanRedo,
} from './history.mjs';
import { makeCalibration } from './measure.mjs';

const LS_PREFIX = 'droppin.markup.v1.';
const REMOTE_SAVE_DEBOUNCE_MS = 800;

// `remote` (optional): { load: () => Promise<doc|null>, save: (doc) => Promise }
// keyed to the same drawing as storageKey. localStorage stays the offline cache
// + source of truth; a successful remote.load() replaces it, and edits push to
// the server debounced. Any remote failure is swallowed — the app behaves
// exactly as the localStorage-only build when the API is absent/unreachable.
export function useMarkup({ active = false, storageKey = null, remote = null } = {}) {
  const [history, setHistory] = useState(() => createHistory([]));
  const objects = history.present;
  const [selectedIds, setSelectedIds] = useState([]);
  const [tool, setToolState] = useState('select');
  const [measureKind, setMeasureKind] = useState('length');
  const [drawStyle, setDrawStyle] = useState({ stroke: '#e8650a', strokeWidth: 2 });
  const [unit, setUnit] = useState('m');
  const [calibration, setCalibrationState] = useState(null);
  const [calibDraft, setCalibDraft] = useState(null); // {refA,refB} awaiting known length
  const [snapGrid, setSnapGrid] = useState(false);     // light grid-snap aid
  const [showTotals, setShowTotals] = useState(false); // take-off panel toggle
  // The catalogue type being drawn, or null for an untyped mark.
  const [markType, setMarkTypeState] = useState(null);
  // v0.139.0 — an IN-PROGRESS COMMAND (a half-drawn polygon / area, a live drag).
  // MarkupLayer owns the geometry of it and registers how to cancel / finish;
  // this flag is what the toolbar shows a Cancel + Finish pair for, so a touch
  // user with no Esc or Enter key can still back out of a command.
  const [commandActive, setCommandActive] = useState(false);
  const commandRef = useRef({ cancel: null, finish: null });

  // Refs so window-level handlers read live values without re-binding.
  const clipboardRef = useRef([]);
  const selRef = useRef(selectedIds);
  const objRef = useRef(objects);
  const activeRef = useRef(active);
  const moveBaseRef = useRef(null);
  const editBaseRef = useRef(null);
  const loadedKeyRef = useRef(null);
  const skipSaveRef = useRef(false);
  const remoteRef = useRef(remote);
  const saveTimerRef = useRef(null);
  useEffect(() => { selRef.current = selectedIds; }, [selectedIds]);
  useEffect(() => { objRef.current = objects; }, [objects]);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { remoteRef.current = remote; }, [remote]);

  // ---- Persistence (per drawing) ------------------------------------------
  // Load: localStorage first (instant, offline). If a remote is wired AND the
  // local copy is EMPTY, pull the server copy — but only apply it while the
  // drawing is unchanged and the user still hasn't drawn, so a slow server
  // response can never clobber a fresh local edit. When local already has
  // content it WINS (and the debounced save pushes it to the server); a server
  // 404/500/offline is swallowed. This makes cross-device persistence work for
  // the "new device, no local copy" case without ever losing in-progress work.
  useEffect(() => {
    if (!storageKey) return undefined;
    skipSaveRef.current = true;
    let doc = { objects: [], calibration: null, unit: null };
    try {
      const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LS_PREFIX + storageKey);
      if (raw) doc = deserialize(JSON.parse(raw));
    } catch (_) { /* ignore corrupt cache */ }
    setHistory(createHistory(doc.objects || []));
    setCalibrationState(doc.calibration || null);
    if (doc.unit) setUnit(doc.unit);
    setSelectedIds([]);
    setCalibDraft(null);
    loadedKeyRef.current = storageKey;
    const t = setTimeout(() => { skipSaveRef.current = false; }, 0);

    const localEmpty = !(doc.objects && doc.objects.length) && !doc.calibration;
    // 2026-08-14 — ALSO pull when the local copy has marks but NO SCALE. The
    // server can now learn a plan's scale after the fact (re-importing it from
    // InSite backfills one), and this device would otherwise never see it: local
    // was non-empty, so the remote read was skipped and the plan stayed in px
    // forever on the very machine the marks were drawn on. A scale is a fact
    // about the SHEET; the marks are the user's work. Taking the scale and
    // keeping the marks loses nothing either way.
    const wantsScale = !doc.calibration;
    let cancelled = false;
    const r = remoteRef.current;
    if ((localEmpty || wantsScale) && r && typeof r.load === 'function') {
      Promise.resolve().then(() => r.load()).then((rdoc) => {
        if (cancelled || !rdoc || loadedKeyRef.current !== storageKey) return;
        const d2 = deserialize(rdoc);
        if (!(d2.objects && d2.objects.length) && !d2.calibration) return; // server empty too
        // Scale-only adoption: keep every local object exactly as it is and take
        // just the calibration (+ the unit it was measured in).
        if (!localEmpty) {
          if (!d2.calibration) return;                       // nothing to gain
          skipSaveRef.current = true;
          setCalibrationState(d2.calibration);
          if (d2.unit) setUnit(d2.unit);
          setTimeout(() => { if (!cancelled) skipSaveRef.current = false; }, 0);
          return;
        }
        if (objRef.current && objRef.current.length) return; // user started drawing -> keep theirs
        skipSaveRef.current = true;
        setHistory(createHistory(d2.objects || []));
        setCalibrationState(d2.calibration || null);
        if (d2.unit) setUnit(d2.unit);
        setTimeout(() => { if (!cancelled) skipSaveRef.current = false; }, 0);
      }).catch(() => { /* offline / not deployed -> keep localStorage */ });
    }
    return () => { cancelled = true; clearTimeout(t); };
  }, [storageKey]);

  // Save: localStorage synchronously; push to the server debounced (best-effort).
  useEffect(() => {
    if (!storageKey || skipSaveRef.current || loadedKeyRef.current !== storageKey) return;
    let doc;
    try {
      doc = serialize({ objects, calibration, unit });
      localStorage.setItem(LS_PREFIX + storageKey, JSON.stringify(doc));
    } catch (_) { /* quota / unavailable — non-fatal */ }
    const r = remoteRef.current;
    if (doc && r && typeof r.save === 'function') {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        Promise.resolve().then(() => r.save(doc)).catch(() => { /* best-effort */ });
      }, REMOTE_SAVE_DEBOUNCE_MS);
    }
  }, [storageKey, objects, calibration, unit]);

  // ---- History-committing mutators ----------------------------------------
  const commit = useCallback((next) => setHistory((h) => pushHistory(h, next)), []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);
  const select = useCallback((ids, additive = false) => {
    const arr = Array.isArray(ids) ? ids : (ids == null ? [] : [ids]);
    const norm = arr.map(String);
    setSelectedIds((prev) => (additive ? Array.from(new Set([...prev, ...norm])) : norm));
  }, []);

  const addObject = useCallback((obj) => {
    setHistory((h) => pushHistory(h, [...h.present, obj]));
    setSelectedIds([String(obj.id)]);
    return obj.id;
  }, []);

  const updateObject = useCallback((id, patch) => {
    setHistory((h) => pushHistory(h, h.present.map((o) => (String(o.id) === String(id) ? { ...o, ...patch } : o))));
  }, []);

  // Several marks changed by one action (a type given to a whole selection):
  // one history step, so one Undo puts every one back.
  const updateObjects = useCallback((patches) => {
    if (!(patches || []).some(Boolean)) return;
    setHistory((h) => pushHistory(h, mergePatches(h.present, patches)));
  }, []);

  const updateText = useCallback((id, text) => {
    setHistory((h) => pushHistory(h, h.present.map((o) => (String(o.id) === String(id) ? { ...o, text } : o))));
  }, []);

  const deleteObjects = useCallback((ids) => {
    const set = new Set((ids || []).map(String));
    if (!set.size) return;
    setHistory((h) => pushHistory(h, h.present.filter((o) => !set.has(String(o.id)))));
    setSelectedIds((prev) => prev.filter((id) => !set.has(String(id))));
  }, []);

  const deleteSelected = useCallback(() => { deleteObjects(selRef.current); }, [deleteObjects]);

  // ---- Live move (drag) — one undo step per drag --------------------------
  const beginMove = useCallback(() => { moveBaseRef.current = objRef.current; }, []);
  const previewMove = useCallback((dx, dy) => {
    const base = moveBaseRef.current; if (!base) return;
    const set = new Set(selRef.current.map(String));
    setHistory((h) => replacePresent(h, base.map((o) => (set.has(String(o.id)) ? translateObject(o, dx, dy) : o))));
  }, []);
  const commitMove = useCallback((dx, dy) => {
    const base = moveBaseRef.current; if (!base) return;
    moveBaseRef.current = null;
    // A click that never moved (dx=dy=0) must NOT create an undo entry — just
    // restore the untouched base snapshot (pure selection, no geometry change).
    if (dx === 0 && dy === 0) { setHistory((h) => replacePresent(h, base)); return; }
    const set = new Set(selRef.current.map(String));
    const next = base.map((o) => (set.has(String(o.id)) ? translateObject(o, dx, dy) : o));
    setHistory((h) => commitInteraction(h, base, next));
  }, []);

  // ---- Live edit (resize / reshape) — one undo step per drag --------------
  // `patch` is a shallow patch merged onto the ONE object `id` (e.g. { points }
  // for reshape, or { style } for a text-box font resize — the caller passes a
  // complete merged style). Same preview/commit discipline as move.
  const beginEdit = useCallback(() => { editBaseRef.current = objRef.current; }, []);
  const previewEdit = useCallback((id, patch) => {
    const base = editBaseRef.current; if (!base) return;
    setHistory((h) => replacePresent(h, base.map((o) => (String(o.id) === String(id) ? { ...o, ...patch } : o))));
  }, []);
  const commitEdit = useCallback((id, patch, changed = true) => {
    const base = editBaseRef.current; if (!base) return;
    editBaseRef.current = null;
    if (!changed) { setHistory((h) => replacePresent(h, base)); return; }
    const next = base.map((o) => (String(o.id) === String(id) ? { ...o, ...patch } : o));
    setHistory((h) => commitInteraction(h, base, next));
  }, []);

  // ---- Clipboard ----------------------------------------------------------
  const copy = useCallback(() => {
    const clip = copyObjects(objRef.current, selRef.current);
    if (clip.length) clipboardRef.current = clip;
  }, []);
  const cut = useCallback(() => {
    if (!selRef.current.length) return;
    const { remaining, clipboard } = cutObjects(objRef.current, selRef.current);
    if (clipboard.length) { clipboardRef.current = clipboard; commit(remaining); setSelectedIds([]); }
  }, [commit]);
  const paste = useCallback(() => {
    const clip = clipboardRef.current;
    if (!clip || !clip.length) return;
    const { objects: next, pastedIds } = pasteObjects(objRef.current, clip, {});
    commit(next);
    setSelectedIds(pastedIds.map(String));
  }, [commit]);

  // ---- Undo / redo --------------------------------------------------------
  const undo = useCallback(() => setHistory((h) => hUndo(h)), []);
  const redo = useCallback(() => setHistory((h) => hRedo(h)), []);
  const canUndo = hCanUndo(history);
  const canRedo = hCanRedo(history);

  // ---- Tool + style + unit ------------------------------------------------
  const setTool = useCallback((t) => {
    setToolState(t);
    if (t !== 'select') setSelectedIds([]);
    if (t !== 'calibrate') setCalibDraft(null);
    // Picking a raw tool by hand drops the typed one: the next mark is a plain
    // area, not silently still "new tiles". A quantity attributed to the wrong
    // type is worse than one attributed to none.
    if (t !== 'select' && t !== 'pan') setMarkTypeState(null);
  }, []);

  // ---- The MARK TYPE being drawn (2026-08-14) -----------------------------
  // From the tenant's catalogue: { key, label, color, kind }. Choosing one is
  // choosing a tool AND a colour AND what the mark will be totalled as — which
  // is the whole point, and why it is one action rather than three.
  const setMarkType = useCallback((t) => {
    if (!t || !t.key) { setMarkTypeState(null); return; }
    setMarkTypeState(t);
    setSelectedIds([]);
    setCalibDraft(null);
    // The kind decides the tool: an area type draws areas, a run draws lengths,
    // a point drops a pin.
    if (t.kind === 'area') { setToolState('measure'); setMeasureKind('area'); }
    else if (t.kind === 'length') { setToolState('measure'); setMeasureKind('length'); }
    else { setToolState('polygon'); }
    if (t.color) setDrawStyle((s) => ({ ...s, stroke: t.color }));
  }, []);

  // ---- Calibration flow (draw a line -> enter its real length) ------------
  const setCalibrationLine = useCallback((refA, refB) => setCalibDraft({ refA, refB }), []);
  const confirmCalibration = useCallback((knownMm) => {
    setCalibDraft((d) => {
      if (d && knownMm > 0) {
        const cal = makeCalibration(d.refA, d.refB, knownMm);
        if (cal) setCalibrationState(cal);
      }
      return null;
    });
    setToolState('select');
  }, []);
  const cancelCalibration = useCallback(() => { setCalibDraft(null); setToolState('select'); }, []);
  const clearCalibration = useCallback(() => setCalibrationState(null), []);

  // ---- In-progress command (registered by MarkupLayer) --------------------
  // The layer calls this whenever a command starts or ends; `handlers` is
  // { cancel, finish } while one is running and null when none is.
  const registerCommand = useCallback((handlers) => {
    commandRef.current = handlers || { cancel: null, finish: null };
    setCommandActive(!!(handlers && handlers.cancel));
  }, []);
  // Cancel returns TRUE when it actually had something to cancel, so callers
  // (Escape) can tell "backed out of a command" from "nothing was running".
  const cancelCommand = useCallback(() => {
    const c = commandRef.current && commandRef.current.cancel;
    if (!c) return false;
    c();
    return true;
  }, []);
  const finishCommand = useCallback(() => {
    const f = commandRef.current && commandRef.current.finish;
    if (f) f();
  }, []);

  // ---- Hit-test passthrough (layer uses for click-select) -----------------
  const hitTopmost = useCallback((pPx, tolPx, W, H) => modelHitTopmost(objRef.current, pPx, tolPx, W, H), []);

  const serializeDoc = useCallback(() => serialize({ objects: objRef.current, calibration, unit }), [calibration, unit]);

  // ---- Keyboard shortcuts (only while markup is active) -------------------
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (!activeRef.current) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      const meta = e.ctrlKey || e.metaKey;
      const k = (e.key || '').toLowerCase();
      if (meta && k === 'c') { copy(); }
      else if (meta && k === 'x') { cut(); e.preventDefault(); }
      else if (meta && k === 'v') { paste(); e.preventDefault(); }
      else if (meta && k === 'z') { if (e.shiftKey) redo(); else undo(); e.preventDefault(); }
      else if (meta && k === 'y') { redo(); e.preventDefault(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selRef.current.length) { deleteSelected(); e.preventDefault(); }
      } else if (e.key === 'Escape') {
        // CAD behaviour: the first Escape backs out of the command you are in
        // the middle of (a half-drawn area, a live drag) and LEAVES the tool
        // armed so you can start again. Only when nothing is running does it
        // drop the selection and fall back to the select tool.
        if (cancelCommand()) { e.preventDefault(); return; }
        setSelectedIds([]); setToolState('select'); setCalibDraft(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, copy, cut, paste, undo, redo, deleteSelected, cancelCommand]);

  return useMemo(() => ({
    // state
    objects, selectedIds, tool, measureKind, drawStyle, unit, calibration, calibDraft,
    snapGrid, showTotals, canUndo, canRedo, count: objects.length, commandActive, markType,
    // selection + tool
    setTool, setMarkType, select, clearSelection, setMeasureKind, setDrawStyle, setUnit,
    setSnapGrid, setShowTotals,
    // in-progress command
    registerCommand, cancelCommand, finishCommand,
    // objects
    addObject, updateObject, updateObjects, updateText, deleteObjects, deleteSelected, hitTopmost,
    // move + edit
    beginMove, previewMove, commitMove, beginEdit, previewEdit, commitEdit,
    // clipboard + history
    copy, cut, paste, undo, redo,
    // calibration
    setCalibrationLine, confirmCalibration, cancelCalibration, clearCalibration,
    // persistence
    serializeDoc,
  }), [
    objects, selectedIds, tool, measureKind, drawStyle, unit, calibration, calibDraft,
    snapGrid, showTotals, canUndo, canRedo, commandActive, markType, setMarkType,
    registerCommand, cancelCommand, finishCommand,
    setTool, select, clearSelection, addObject, updateObject, updateObjects, updateText,
    deleteObjects, deleteSelected, hitTopmost,
    beginMove, previewMove, commitMove, beginEdit, previewEdit, commitEdit,
    copy, cut, paste, undo, redo,
    setCalibrationLine, confirmCalibration, cancelCalibration, clearCalibration, serializeDoc,
  ]);
}
